import * as styles from "./ExonnHub.styles.ts";
import { ContentView } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ContentView/ContentView.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";

import { ExonnHubProvider } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHubProvider.tsx";
import { useTranslation } from "react-i18next";
import { EmptyState } from "src/components/EmptyState/EmptyState.tsx";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { useExonnHubOrder } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubOrder.ts";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { ExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHubStore.ts";
import { FiltersView } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/FiltersView/FiltersView.tsx";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { FloatingOverlay } from "@floating-ui/react";
import { reaction } from "mobx";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import type { ExonnHubFilterModel } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.types.ts";
import { B2bCatalogErrorBoundary } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/B2bCatalogErrorBoundary/B2bCatalogErrorBoundary.tsx";
import { ErrorBoundary } from "react-error-boundary";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

await loadTranslations(TranslationNamespaceEnum.ExonnHubComponent);

type B2bGateCatalogProps = {
  orderId: string;
  onClose?: () => void;
  onFullscreenChange?: (fullscreen: boolean) => void;
  initialIsFullscreen?: boolean;
  initialFilterModels?: ExonnHubFilterModel[];
};

const B2bGateCatalog = observer((props: B2bGateCatalogProps) => {
  const {
    initialFilterModels,
    initialIsFullscreen,
    onFullscreenChange,
    ...restProps
  } = props;
  const [store] = useState(
    () =>
      new ExonnHubStore({
        filtersModels: initialFilterModels,
        isFullscreen: initialIsFullscreen,
      }),
  );
  useEffect(() => {
    return reaction(
      () => store.isFullscreen,
      (isFullscreen) => {
        onFullscreenChange?.(isFullscreen);
      },
    );
  }, [onFullscreenChange, store]);

  return (
    <div
      css={styles.outer({
        isFullscreen: store.isFullscreen,
      })}
    >
      <ErrorBoundary FallbackComponent={B2bCatalogErrorBoundary}>
        <B2bGateCatalogImpl {...restProps} store={store} />
      </ErrorBoundary>
    </div>
  );
});

type B2bGateCatalogImplProps = {
  orderId: string;
  onClose?: () => void;
  store: ExonnHubStore;
};

export const B2bGateCatalogImpl = observer((props: B2bGateCatalogImplProps) => {
  const { orderId, store } = props;
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  const theme = useTheme();
  const isLessThanIpadPro = useMediaQuery(
    theme.deviceV2.lessThan.ipadProLandscape,
  );
  const isLessThanLaptop = useMediaQuery(theme.deviceV2.lessThan.laptop);

  const { error: orderError, refetch: refetchOrder } = useExonnHubOrder({
    orderId,
  });

  useEffect(() => {
    return reaction(
      () => store.isFullscreen,
      (isFullscreen) => {
        if (
          isLessThanLaptop &&
          !isFullscreen &&
          store.isFilterPanelOpen &&
          store.isProductPanelOpen
        ) {
          store.closeFilterPanel();
        }
        if (
          isLessThanLaptop &&
          isFullscreen &&
          store.isFilterPanelOpen &&
          store.isProductPanelOpen
        ) {
          store.closeFilterPanel();
        }
      },
    );
  }, [isLessThanIpadPro, isLessThanLaptop, store]);

  useEffect(() => {
    return reaction(
      () => ({
        isProductPanelOpen: store.isProductPanelOpen,
        isFullscreen: store.isFullscreen,
      }),
      ({ isFullscreen, isProductPanelOpen }) => {
        if (isProductPanelOpen && isLessThanIpadPro && !isFullscreen) {
          store.closeFilterPanel();
        }
        if (isProductPanelOpen && isLessThanLaptop && isFullscreen) {
          store.closeFilterPanel();
        }
      },
    );
  }, [store, isLessThanLaptop, isLessThanIpadPro]);

  useEffect(() => {
    return reaction(
      () => ({
        isFullscreen: store.isFullscreen,
        isFilterPanelOpen: store.isFilterPanelOpen,
      }),
      ({ isFullscreen, isFilterPanelOpen }) => {
        if (isFilterPanelOpen && isLessThanIpadPro && !isFullscreen) {
          store.closeProductPanel();
        }
        if (isFilterPanelOpen && isLessThanLaptop && isFullscreen) {
          store.closeProductPanel();
        }
      },
    );
  }, [store, isLessThanIpadPro, isLessThanLaptop]);

  if (orderError) {
    return (
      <EmptyState
        title={t("errors.loadOrderDataFailure.title")}
        description={t("errors.loadOrderDataFailure.description")}
        type={"warning"}
        action={
          <ButtonV2
            variant={"filled"}
            onClick={() => {
              refetchOrder();
            }}
          >
            {t("errors.loadOrderDataFailure.action")}
          </ButtonV2>
        }
      />
    );
  }

  return (
    <ExonnHubProvider orderId={orderId} store={store}>
      <div css={styles.root({ isFullscreen: store.isFullscreen })}>
        <FiltersView />
        <ContentView />
        <OrderPreviewOverlay />
      </div>
    </ExonnHubProvider>
  );
});

const OrderPreviewOverlay = observer(() => {
  const store = useExonnHubStore();
  if (!store.isOrderPreviewOpen) {
    return null;
  }
  return (
    <FloatingOverlay
      css={styles.orderPreviewOverlay}
      onClick={() => {
        store.setIsOrderPreviewOpen(false);
      }}
    />
  );
});

export const ExonnHub = B2bGateCatalog;
